import { RedoCartCheckoutStatus } from "@redotech/cockroachdb-codegen/tables/marketplace/redo-cart-checkout-status";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import {
  redoCartCheckoutIdFormat,
  redoCartIdFormat,
} from "@redotech/redo-marketplace-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RedoCartSchema } from "./redo-cart";

const RedoCheckoutSchema_ = z.object({
  id: redoCartCheckoutIdFormat.schema,
  cartId: redoCartIdFormat.schema,
  status: createEnum(RedoCartCheckoutStatus, {
    enumName: "RedoCartCheckoutStatus",
  }),
  currency: z.string(),
  subtotal: z.number().nullable(),
  totalTax: z.number().nullable(),
  shipping: z.number().nullable(),
  total: z.number().nullable(),
  destinationWarehouseLocationId: locationIdFormat.schema.nullable(),
  destinationCustomAddressId: z.string().nullable(),
  redoOrderId: z.string().nullable(),
});
export const RedoCheckoutSchema: z.ZodType<RedoCheckout> = RedoCheckoutSchema_;
export interface RedoCheckout extends z.infer<typeof RedoCheckoutSchema_> {}

const RedoCheckoutShippingAddressSchema_ = z.object({
  name: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
});
export const RedoCheckoutShippingAddressSchema: z.ZodType<RedoCheckoutShippingAddress> =
  RedoCheckoutShippingAddressSchema_;
export interface RedoCheckoutShippingAddress extends z.infer<
  typeof RedoCheckoutShippingAddressSchema_
> {}

// Funds gate for placing the order, mirroring the OMS label "needs funds"
// check: the post-charge balance must stay at/above the merchant's minimum
// balance. "notSetUp" means the team has no outbound-labels balance at all.
export const SuppliesFundsStatusSchema = z.enum([
  "sufficient",
  "insufficient",
  "notSetUp",
]);
export type SuppliesFundsStatus = z.infer<typeof SuppliesFundsStatusSchema>;

export const getRedoCheckoutData = {
  name: "getRedoCheckoutData" as const,
  input: z.object({ checkoutId: redoCartCheckoutIdFormat.schema }),
  output: z.object({
    checkout: RedoCheckoutSchema,
    // A checkout always has its cart (cart_id is a NOT NULL FK).
    cart: RedoCartSchema,
    // The resolved destination: an OMS warehouse address (live), or a saved
    // custom address (non-OMS). Null when a non-OMS merchant hasn't entered one
    // yet — that's the signal to show the address form.
    shippingAddress: RedoCheckoutShippingAddressSchema.nullable(),
    // Whether the merchant can cover the order against their OMS balance. Null
    // until the checkout is quoted (total known).
    funds: SuppliesFundsStatusSchema.nullable(),
  }),
};
